import type { ChildrenProps } from "@msuite/ui/types";
import { cn } from "@msuite/ui/utils";

/** Props Interface */
interface WikiPageLayoutProps extends ChildrenProps {
	/**
	 * A companion to the document — the page's thread. On a wide panel it stands to the right of
	 * the column and stays put while the document scrolls; on a narrow one it follows beneath.
	 */
	aside?: React.ReactNode;
	/**
	 * Give the column the panel's full height instead of letting it scroll. The editor uses
	 * this: its text box fills the height and scrolls within itself, so the trail, the buttons and
	 * the title stay pinned while the author works far down the document. The panel does not
	 * scroll in this state, so a thread that would follow beneath the column has nowhere to go;
	 * it is left out until the panel is wide enough to seat it beside.
	 */
	fill?: boolean;
}

/**
 * The document column every state of a wiki page shares, reading and editing alike, so that
 * switching between them changes what the author can do and not where anything sits. It hugs
 * the sidebar rather than centring, because on a wide screen a centred column floats in the
 * middle of the panel with nothing anchoring it. The panel has a fixed height, so the column
 * scrolls inside it; a handful of videos is enough to make a page taller than the screen.
 *
 * The breakpoint is a container query on the panel, not a viewport one: the sidebar takes a
 * share of the window, and what decides whether two columns fit is the width left over.
 */
export function WikiPageLayout({ children, aside, fill = false }: WikiPageLayoutProps) {
	/** Render */
	return (
		<div
			className={cn(
				"card p-0",
				"@container h-full min-h-0",
				fill ? "overflow-hidden" : "overflow-y-auto overflow-x-hidden",
			)}
		>
			{/* The panes stretch rather than sitting at the top, which is what gives the column a
			    height to divide: an item aligned to the start of the row is as tall as its own
			    content, so a document inside it grows the panel instead of scrolling in it. */}
			<div className={cn("h-full", "flex flex-col @min-[1200px]:flex-row")}>
				{/* The column keeps its reading measure in every state. It does not grow along the
				    row: made `flex-1` next to the thread, the two split the row in half, and the
				    document stood narrower beside its thread than without it — which is why the page
				    widened the moment editing began. Beneath the breakpoint the filling column does
				    grow, downwards, to hand the panel's height to its text box. */}
				<div
					className={cn(
						"flex w-full min-w-0 max-w-[880px] flex-col",
						"p-4",
						fill ? "min-h-0 flex-1 @min-[1200px]:flex-initial" : "min-h-full",
					)}
				>
					{children}
				</div>
				{/* Beside the document the thread takes the width the reading column leaves over —
				    never less than a usable comment column, never wider than comment lines stay
				    readable. The breakpoint is where the column reaches that minimum with the
				    document still near its reading measure. */}
				{aside && (
					<aside
						className={cn(
							"h-full w-full max-w-[880px]",
							"@min-[1200px]:sticky @min-[1200px]:top-0 @min-[1200px]:min-w-[24rem] @min-[1200px]:flex-1",
							fill && "hidden @min-[1200px]:block",
						)}
					>
						{aside}
					</aside>
				)}
			</div>
		</div>
	);
}
